/**
 * Chart section: a metric tab-strip + a single/grid layout toggle over the reusable
 * CompareMetricChart, plus loading/error/empty/no-data handling. One metric per chart
 * (heterogeneous scales) — the table is the all-metrics scan.
 * For Media Devoted | Created By Ciro Autiero
 */

import { LayoutGrid, LineChart as LineChartIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { AlignedRow } from '../../data/types';
import type { CompareMetric } from '../../metrics/compareMetrics';
import type { ChartLayout } from '../../state/useDashboardCompareState';
import { CompareMetricChart } from './CompareMetricChart';
import { CompareChartState } from './CompareChartStates';

interface Props {
  metrics: CompareMetric[];
  aligned: AlignedRow[];
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  currency: string;
  labelA: string;
  labelB: string;
  chartMetricKey: string;
  onChartMetric: (key: string) => void;
  chartLayout: ChartLayout;
  onLayout: (layout: ChartLayout) => void;
  onRetry: () => void;
}

function LayoutToggle({ value, onChange }: { value: ChartLayout; onChange: (l: ChartLayout) => void }) {
  const opts = [
    { value: 'single' as const, Icon: LineChartIcon, label: 'Single' },
    { value: 'grid' as const, Icon: LayoutGrid, label: 'Grid' },
  ];
  return (
    <div className="flex gap-1" role="group" aria-label="Chart layout">
      {opts.map(({ value: v, Icon, label }) => (
        <button
          key={v}
          type="button"
          title={label}
          aria-pressed={v === value}
          onClick={() => onChange(v)}
          className={cn(
            'rounded-md border p-1.5 transition-colors',
            v === value
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

export function CompareChartPanel(props: Props) {
  const { metrics, aligned, isLoading, isError, hasData } = props;
  const { currency, labelA, labelB, chartMetricKey, onChartMetric } = props;
  const { chartLayout, onLayout, onRetry } = props;

  if (isLoading) return <CompareChartState variant="loading" />;
  if (isError) return <CompareChartState variant="error" onRetry={onRetry} />;
  if (metrics.length === 0) return <CompareChartState variant="empty" />;
  if (!hasData) return <CompareChartState variant="no-data" />;

  const active = metrics.find((m) => m.key === chartMetricKey) ?? metrics[0];
  if (!active) return <CompareChartState variant="empty" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Chart metric">
          {metrics.map((m) => {
            const selected = m.key === active.key && chartLayout === 'single';
            return (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  onChartMetric(m.key);
                  onLayout('single');
                }}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  selected
                    ? 'border-primary bg-primary/10 text-foreground ring-2 ring-primary/25'
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50',
                )}
              >
                {m.shortLabel}
              </button>
            );
          })}
        </div>
        <LayoutToggle value={chartLayout} onChange={onLayout} />
      </div>

      {chartLayout === 'grid' ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {metrics.map((m) => (
            <CompareMetricChart
              key={m.key}
              metric={m}
              aligned={aligned}
              currency={currency}
              labelA={labelA}
              labelB={labelB}
              compact
            />
          ))}
        </div>
      ) : (
        <CompareMetricChart
          metric={active}
          aligned={aligned}
          currency={currency}
          labelA={labelA}
          labelB={labelB}
        />
      )}
    </div>
  );
}

export default CompareChartPanel;
